import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Close as CloseIcon } from "@mui/icons-material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Plugin,
} from "chart.js";
import { getConvertsScannerHistoricalData } from "../../../services/Axios";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Plugin to draw tracking line across all charts and handle hover
const createTrackingLinePlugin = (trackingX, setTrackingX, theme) => ({
  id: 'trackingLinePlugin',
  afterDraw: (chart) => {
    if (trackingX === null) return;
    
    const ctx = chart.ctx;
    const chartArea = chart.chartArea;
    if (!chartArea) return;
    
    // Only draw if trackingX is within the chart area
    if (trackingX >= chartArea.left && trackingX <= chartArea.right) {
      ctx.save();
      ctx.strokeStyle = theme.palette.text.secondary;
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.moveTo(trackingX, chartArea.top);
      ctx.lineTo(trackingX, chartArea.bottom);
      ctx.stroke();
      ctx.restore();
    }
  },
  afterEvent: (chart, event) => {
    if (!event.event) return;
    
    if (event.type === 'mousemove') {
      const canvasPosition = ChartJS.helpers.getRelativePosition(event.event, chart);
      const chartArea = chart.chartArea;
      
      if (chartArea && canvasPosition.x >= chartArea.left && canvasPosition.x <= chartArea.right) {
        setTrackingX(canvasPosition.x);
      }
    } else if (event.type === 'mouseout') {
      setTrackingX(null);
    }
  },
});

// Plugin to draw dashed zero line and filled areas for expansion/contraction charts
const createZeroLinePlugin = (theme) => ({
  id: 'zeroLinePlugin',
  beforeDatasetsDraw: (chart) => {
    // Only apply to charts that have this plugin enabled
    if (!chart.options.plugins?.zeroLinePlugin?.enabled) {
      return;
    }
    
    const ctx = chart.ctx;
    const chartArea = chart.chartArea;
    if (!chartArea) return;
    
    const yScale = chart.scales.y;
    if (!yScale) return;
    
    // Get the main dataset (should be the last one with actual data)
    const mainDataset = chart.data.datasets.find(ds => ds.label === "Expansion/Contraction (Mid Px - Nuked Px)");
    if (!mainDataset || !mainDataset.data) return;
    
    const mainMeta = chart.getDatasetMeta(chart.data.datasets.indexOf(mainDataset));
    if (!mainMeta || !mainMeta.data || mainMeta.data.length === 0) return;
    
    try {
      const zeroY = yScale.getPixelForValue(0);
      
      ctx.save();
      
      // Draw green fill for positive values
      ctx.fillStyle = theme.palette.success.main + "40";
      ctx.beginPath();
      let inPositiveSegment = false;
      
      for (let i = 0; i < mainMeta.data.length; i++) {
        const point = mainMeta.data[i];
        const value = mainDataset.data[i];
        
        if (point && value !== null && !isNaN(value)) {
          if (value >= 0) {
            if (!inPositiveSegment) {
              ctx.moveTo(point.x, zeroY);
              inPositiveSegment = true;
            }
            ctx.lineTo(point.x, point.y);
          } else {
            if (inPositiveSegment) {
              ctx.lineTo(point.x, zeroY);
              ctx.closePath();
              ctx.fill();
              ctx.beginPath();
              inPositiveSegment = false;
            }
          }
        }
      }
      
      // Close any remaining positive segment
      if (inPositiveSegment) {
        const lastPoint = mainMeta.data[mainMeta.data.length - 1];
        if (lastPoint) {
          ctx.lineTo(lastPoint.x, zeroY);
        }
        ctx.closePath();
        ctx.fill();
      }
      
      // Draw red fill for negative values
      ctx.fillStyle = theme.palette.error.main + "40";
      ctx.beginPath();
      let inNegativeSegment = false;
      
      for (let i = 0; i < mainMeta.data.length; i++) {
        const point = mainMeta.data[i];
        const value = mainDataset.data[i];
        
        if (point && value !== null && !isNaN(value)) {
          if (value < 0) {
            if (!inNegativeSegment) {
              ctx.moveTo(point.x, zeroY);
              inNegativeSegment = true;
            }
            ctx.lineTo(point.x, point.y);
          } else {
            if (inNegativeSegment) {
              ctx.lineTo(point.x, zeroY);
              ctx.closePath();
              ctx.fill();
              ctx.beginPath();
              inNegativeSegment = false;
            }
          }
        }
      }
      
      // Close any remaining negative segment
      if (inNegativeSegment) {
        const lastPoint = mainMeta.data[mainMeta.data.length - 1];
        if (lastPoint) {
          ctx.lineTo(lastPoint.x, zeroY);
        }
        ctx.closePath();
        ctx.fill();
      }
      
      ctx.restore();
    } catch (error) {
      console.error('Error drawing filled areas:', error);
    }
  },
  afterDraw: (chart) => {
    // Only apply to charts that have this plugin enabled
    if (!chart.options.plugins?.zeroLinePlugin?.enabled) {
      return;
    }
    
    const ctx = chart.ctx;
    const chartArea = chart.chartArea;
    if (!chartArea) return;
    
    const yScale = chart.scales.y;
    if (!yScale) return;
    
    try {
      const zeroY = yScale.getPixelForValue(0);
      
      // Only draw if zero is within the chart area
      if (zeroY >= chartArea.top && zeroY <= chartArea.bottom) {
        ctx.save();
        ctx.strokeStyle = theme.palette.text.primary;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]); // Dashed line pattern
        ctx.beginPath();
        ctx.moveTo(chartArea.left, zeroY);
        ctx.lineTo(chartArea.right, zeroY);
        ctx.stroke();
        ctx.restore();
      }
    } catch (error) {
      console.error('Error drawing zero line:', error);
    }
  },
});

const BondDetailsModal = ({ open, onClose, bondData }) => {
  const theme = useTheme();
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trackingX, setTrackingX] = useState(null); // X position for tracking line
  const chartRefs = useRef({}); // Store refs to all chart instances
  const [overrideMidPx, setOverrideMidPx] = useState(null);
  const [overrideStockPx, setOverrideStockPx] = useState(null);
  const [overrideDelta, setOverrideDelta] = useState(null);

  useEffect(() => {
    if (open && bondData?.isin) {
      fetchHistoricalData();
      // Reset overrides when modal opens
      setOverrideMidPx(null);
      setOverrideStockPx(null);
      setOverrideDelta(null);
    } else {
      setHistoricalData([]);
      setError(null);
      setOverrideMidPx(null);
      setOverrideStockPx(null);
      setOverrideDelta(null);
    }
  }, [open, bondData?.isin]);

  const fetchHistoricalData = async () => {
    if (!bondData?.isin) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getConvertsScannerHistoricalData(bondData.isin);
      if (response?.data) {
        setHistoricalData(response.data);
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Error loading historical data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!bondData) {
    return null;
  }

  const formatNumber = (value, decimals = 0) => {
    if (value === null || value === undefined || value === "") return "N/A";
    const numValue = Number(value);
    if (isNaN(numValue)) return "N/A";
    return numValue.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return "N/A";
    }
  };

  const createChartData = (nukedPriceField, label) => {
    const labels = historicalData
      .map((item) => {
        if (!item.quote_date) return null;
        try {
          return new Date(item.quote_date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
        } catch {
          return null;
        }
      })
      .filter((label) => label !== null);

    const midPxData = historicalData.map((item) => {
      const value = Number(item.mid_px);
      return isNaN(value) ? null : value;
    });

    const nukedPxData = historicalData.map((item) => {
      const value = Number(item[nukedPriceField]);
      return isNaN(value) ? null : value;
    });

    const stockPxData = historicalData.map((item) => {
      const value = Number(item.stock_px);
      return isNaN(value) ? null : value;
    });

    return {
      labels,
      datasets: [
        {
          label: "Mid Px",
          data: midPxData,
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.primary.main + "20",
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 4,
          yAxisID: "y",
        },
        {
          label: label,
          data: nukedPxData,
          borderColor: theme.palette.secondary.main,
          backgroundColor: theme.palette.secondary.main + "20",
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 4,
          yAxisID: "y",
        },
        {
          label: "Stock Px",
          data: stockPxData,
          borderColor: theme.palette.success.main,
          backgroundColor: theme.palette.success.main + "20",
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 4,
          yAxisID: "y1",
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        right: 10, // Consistent right padding for alignment
      },
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        display: false, // Hide X-axis on price chart
        title: {
          display: false,
        },
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Bond Price",
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        title: {
          display: true,
          text: "Stock Price",
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          padding: 5,
          font: {
            size: 10,
          },
        },
        afterFit: (scale) => {
          // Ensure consistent width for alignment
          scale.width = 50;
        },
      },
    },
  };

  const createDeltaChartData = (deltaField) => {
    const labels = historicalData
      .map((item) => {
        if (!item.quote_date) return null;
        try {
          return new Date(item.quote_date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
        } catch {
          return null;
        }
      })
      .filter((label) => label !== null);

    const deltaData = historicalData.map((item) => {
      const value = Number(item.delta);
      return isNaN(value) ? null : value;
    });

    const deltaHistoricalData = historicalData.map((item) => {
      const value = Number(item[deltaField]);
      return isNaN(value) ? null : value;
    });

    // Format the label: delta_1d_ago -> "Delta 1d ago", delta_1w_ago -> "Delta 1w ago", etc.
    const formatDeltaLabel = (field) => {
      return field.replace("delta_", "Delta ").replace("_ago", " ago");
    };

    return {
      labels,
      datasets: [
        {
          label: "Delta",
          data: deltaData,
          borderColor: theme.palette.info.main,
          backgroundColor: theme.palette.info.main + "20",
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 4,
        },
        {
          label: formatDeltaLabel(deltaField),
          data: deltaHistoricalData,
          borderColor: theme.palette.warning.main,
          backgroundColor: theme.palette.warning.main + "20",
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 4,
        },
      ],
    };
  };

  const createExpansionContractionChartData = (nukedPriceField) => {
    const labels = historicalData
      .map((item) => {
        if (!item.quote_date) return null;
        try {
          return new Date(item.quote_date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
        } catch {
          return null;
        }
      })
      .filter((label) => label !== null);

    // Calculate difference: Mid Px - Nuked Px from X ago
    const expansionContractionData = historicalData.map((item) => {
      const midPx = Number(item.mid_px);
      const nukedPx = Number(item[nukedPriceField]);
      if (isNaN(midPx) || isNaN(nukedPx)) return null;
      return midPx - nukedPx;
    });

    return {
      labels,
      datasets: [
        {
          label: "Expansion/Contraction (Mid Px - Nuked Px)",
          data: expansionContractionData,
          borderColor: theme.palette.primary.main,
          backgroundColor: "transparent",
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: false, // Don't fill - plugin will handle it
        },
      ],
    };
  };

  const deltaChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        right: 10, // Consistent right padding for alignment
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 10,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        display: false, // Hide X-axis on delta chart too
        title: {
          display: false,
        },
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
      y: {
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Delta",
        },
        ticks: {
          font: {
            size: 10,
          },
          padding: 5,
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        title: {
          display: false,
        },
        ticks: {
          display: false,
        },
        grid: {
          display: false,
          drawOnChartArea: false,
        },
        afterFit: (scale) => {
          // Ensure consistent width for alignment with top chart
          scale.width = 50;
        },
        // This hidden axis ensures alignment with top chart's right axis
      },
    },
  };

  const expansionContractionChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        right: 10, // Consistent right padding for alignment
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 10,
          font: {
            size: 11,
          },
          filter: (item) => {
            // Only show the main line in legend, hide the fill datasets
            return item.text === "Expansion/Contraction (Mid Px - Nuked Px)";
          },
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        filter: (tooltipItem) => {
          // Only show tooltip for the main line dataset (index 0)
          return tooltipItem.datasetIndex === 0;
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: "Date",
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 10,
          },
        },
      },
      y: {
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Price Difference",
        },
        ticks: {
          font: {
            size: 10,
          },
          padding: 5,
        },
        // Ensure zero is always in the scale range
        min: (context) => {
          const values = context.chart.data.datasets[0].data.filter(v => v !== null && !isNaN(v));
          if (values.length === 0) return -1;
          const min = Math.min(...values, 0);
          return min < 0 ? min * 1.1 : -1; // Add padding and ensure zero is visible
        },
        max: (context) => {
          const values = context.chart.data.datasets[0].data.filter(v => v !== null && !isNaN(v));
          if (values.length === 0) return 1;
          const max = Math.max(...values, 0);
          return max > 0 ? max * 1.1 : 1; // Add padding and ensure zero is visible
        },
        // Hide zero grid line since we're drawing it with a plugin
        grid: {
          color: (context) => {
            if (!context || !context.tick) {
              return theme.palette.divider + "40";
            }
            const tickValue = context.tick.value;
            if (tickValue === 0) {
              return "transparent"; // Hide default zero grid line, plugin will draw dashed one
            }
            return theme.palette.divider + "40";
          },
          lineWidth: 1,
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        title: {
          display: false,
        },
        ticks: {
          display: false,
        },
        grid: {
          display: false,
          drawOnChartArea: false,
        },
        afterFit: (scale) => {
          // Ensure consistent width for alignment with top chart
          scale.width = 50;
        },
        // This hidden axis ensures alignment with top chart's right axis
      },
    },
  };

  const chartConfigs = [
    {
      field: "nuked_price_from_1d_ago",
      title: "Nuked Px from 1d ago",
      deltaField: "delta_1d_ago",
      period: "1d",
      midPxField: "mid_px_1d_ago",
      stockPxField: "stock_px_1d_ago",
      ptsChangeField: "pts_change_from_1d_ago",
    },
    {
      field: "nuked_price_from_1w_ago",
      title: "Nuked Px from 1w ago",
      deltaField: "delta_1w_ago",
      period: "1w",
      midPxField: "mid_px_1w_ago",
      stockPxField: "stock_px_1w_ago",
      ptsChangeField: "pts_change_from_1w_ago",
    },
    {
      field: "nuked_price_from_2w_ago",
      title: "Nuked Px from 2w ago",
      deltaField: "delta_2w_ago",
      period: "2w",
      midPxField: "mid_px_2w_ago",
      stockPxField: "stock_px_2w_ago",
      ptsChangeField: "pts_change_from_2w_ago",
    },
    {
      field: "nuked_price_from_3w_ago",
      title: "Nuked Px from 3w ago",
      deltaField: "delta_3w_ago",
      period: "3w",
      midPxField: "mid_px_3w_ago",
      stockPxField: "stock_px_3w_ago",
      ptsChangeField: "pts_change_from_3w_ago",
    },
    {
      field: "nuked_price_from_1m_ago",
      title: "Nuked Px from 1m ago",
      deltaField: "delta_1m_ago",
      period: "1m",
      midPxField: "mid_px_1m_ago",
      stockPxField: "stock_px_1m_ago",
      ptsChangeField: "pts_change_from_1m_ago",
    },
  ];

  // Calculate nuked_price using current stock price (or override) and delta (or override)
  const calculateNukedPrice = (config) => {
    const currentStockPx = overrideStockPx !== null ? overrideStockPx : Number(bondData?.stock_px);
    const midPxXago = Number(bondData?.[config.midPxField]);
    // Use override delta if set, otherwise use the period-specific delta
    const deltaXago = overrideDelta !== null ? overrideDelta : Number(bondData?.[config.deltaField]);
    const stockPxXago = Number(bondData?.[config.stockPxField]);
    
    if (isNaN(midPxXago) || isNaN(deltaXago) || isNaN(stockPxXago) || isNaN(currentStockPx)) {
      return null;
    }
    
    // Get conversion ratio from GlzConvertAttributesInfo (via bondData.conversion_ratio)
    const conversionRatio = Number(bondData?.conversion_ratio);
    if (isNaN(conversionRatio) || conversionRatio === 0) {
      return null;
    }
    
    // Formula: mid_px_X_ago + (delta_X_ago / 100) * conversion_ratio * (current_stock_px - stock_px_X_ago)
    return midPxXago + (deltaXago / 100) * conversionRatio * (currentStockPx - stockPxXago);
  };

  // Calculate pts_change based on override values or current values
  const calculatePtsChange = (config) => {
    const currentMidPx = overrideMidPx !== null ? overrideMidPx : Number(bondData?.mid_px);
    const nukedPrice = calculateNukedPrice(config);
    
    if (isNaN(currentMidPx) || nukedPrice === null) {
      return null;
    }
    
    return currentMidPx - nukedPrice;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[8],
          height: "100vh",
          maxHeight: "100vh",
          margin: 0,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {bondData.name || "Bond"} Details
        </Typography>
        <Button
          onClick={onClose}
          size="small"
          sx={{ minWidth: "auto", p: 0.5 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ mt: 1, flex: 1, overflowY: "auto", pb: 2 }}>
        {/* Condensed Bond Info */}
        <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 1.5, mb: 2, alignItems: "flex-start" }}>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: "fit-content" }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem", fontWeight: 600 }}>
              ISIN
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
              {bondData.isin || "N/A"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: "fit-content" }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem", fontWeight: 600 }}>
              Underlying
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
              {bondData.underlying_ticker || "N/A"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: "fit-content" }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem", fontWeight: 600 }}>
              Maturity
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
              {formatDate(bondData.maturity)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: "fit-content" }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem", fontWeight: 600 }}>
              Amt Out ($mm)
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
              {formatNumber(bondData.outstanding_amt_mm, 0)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: "fit-content" }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem", fontWeight: 600 }}>
              Conversion Ratio
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
              {(() => {
                const conversionRatio = Number(bondData?.conversion_ratio);
                if (isNaN(conversionRatio) || conversionRatio === 0) return "N/A";
                return formatNumber(conversionRatio, 5);
              })()}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: "fit-content" }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem", fontWeight: 600 }}>
              Last Mid Px
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
              {formatNumber(bondData.mid_px, 2)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: "fit-content" }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem", fontWeight: 600 }}>
              Last Stock Px
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
              {formatNumber(bondData.stock_px, 2)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Override Inputs */}
        <Box sx={{ mb: 2, p: 2, backgroundColor: theme.palette.background.default, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Override Current Prices and Delta (for nuked price and pts change calculation)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Override Mid Px"
                type="number"
                size="small"
                fullWidth
                value={overrideMidPx !== null ? overrideMidPx : ""}
                onChange={(e) => {
                  const value = e.target.value === "" ? null : parseFloat(e.target.value);
                  setOverrideMidPx(isNaN(value) ? null : value);
                }}
                placeholder={formatNumber(bondData?.mid_px, 2)}
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Override Stock Px"
                type="number"
                size="small"
                fullWidth
                value={overrideStockPx !== null ? overrideStockPx : ""}
                onChange={(e) => {
                  const value = e.target.value === "" ? null : parseFloat(e.target.value);
                  setOverrideStockPx(isNaN(value) ? null : value);
                }}
                placeholder={formatNumber(bondData?.stock_px, 2)}
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Override Delta"
                type="number"
                size="small"
                fullWidth
                value={overrideDelta !== null ? overrideDelta : ""}
                onChange={(e) => {
                  const value = e.target.value === "" ? null : parseFloat(e.target.value);
                  setOverrideDelta(isNaN(value) ? null : value);
                }}
                placeholder={formatNumber(bondData?.delta_1d_ago, 0)}
                inputProps={{ step: "0.01" }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Period Data Table */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
            Period Comparison
          </Typography>
          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.background.default }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Period</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Mid Px (X ago)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Stock Px (X ago)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Delta (X ago)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Nuked Px (from X ago)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Current Mid Px</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Current Stock Px</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Pts Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chartConfigs.map((config) => {
                  const currentMidPx = overrideMidPx !== null ? overrideMidPx : Number(bondData?.mid_px);
                  const currentStockPx = overrideStockPx !== null ? overrideStockPx : Number(bondData?.stock_px);
                  const recalculatedNukedPrice = calculateNukedPrice(config);
                  const ptsChange = calculatePtsChange(config);
                  
                  return (
                    <TableRow key={config.period} hover>
                      <TableCell sx={{ fontSize: "0.75rem", fontWeight: 500 }}>{config.period}</TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.75rem" }}>
                        {formatNumber(bondData?.[config.midPxField], 2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.75rem" }}>
                        {formatNumber(bondData?.[config.stockPxField], 2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.75rem", fontWeight: overrideDelta !== null ? 600 : 400 }}>
                        {formatNumber(overrideDelta !== null ? overrideDelta : bondData?.[config.deltaField], 0)}
                        {overrideDelta !== null && (
                          <Typography component="span" sx={{ fontSize: "0.65rem", color: theme.palette.warning.main, ml: 0.5 }}>
                            (override)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.75rem", fontWeight: (overrideStockPx !== null || overrideDelta !== null) ? 600 : 400 }}>
                        {formatNumber(recalculatedNukedPrice, 2)}
                        {(overrideStockPx !== null || overrideDelta !== null) && (
                          <Typography component="span" sx={{ fontSize: "0.65rem", color: theme.palette.warning.main, ml: 0.5 }}>
                            (recalc)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.75rem", fontWeight: overrideMidPx !== null ? 600 : 400 }}>
                        {formatNumber(currentMidPx, 2)}
                        {overrideMidPx !== null && (
                          <Typography component="span" sx={{ fontSize: "0.65rem", color: theme.palette.warning.main, ml: 0.5 }}>
                            (override)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.75rem", fontWeight: overrideStockPx !== null ? 600 : 400 }}>
                        {formatNumber(currentStockPx, 2)}
                        {overrideStockPx !== null && (
                          <Typography component="span" sx={{ fontSize: "0.65rem", color: theme.palette.warning.main, ml: 0.5 }}>
                            (override)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          fontSize: "0.75rem", 
                          fontWeight: 600,
                          color: ptsChange !== null && ptsChange < 0 ? theme.palette.error.main : "inherit"
                        }}
                      >
                        {formatNumber(ptsChange, 2)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Historical Data Section */}
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Historical Data (Last 45 Days)
          </Typography>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && historicalData.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No historical data available for this bond.
            </Alert>
          )}

           {!loading && !error && historicalData.length > 0 && (
             <Box>
               {chartConfigs.map((config, index) => (
                 <Box key={index}>
                   <Paper
                     elevation={3}
                     sx={{
                       p: 3,
                       borderRadius: 2,
                       backgroundColor: theme.palette.background.paper,
                       border: `2px solid ${theme.palette.divider}`,
                       mb: index < chartConfigs.length - 1 ? 4 : 0,
                     }}
                   >
                     <Typography
                       variant="h5"
                       sx={{ mb: 2, fontWeight: 600, fontSize: "1.5rem" }}
                     >
                       {config.title}
                     </Typography>
                     <Box 
                       sx={{ 
                         height: 300, 
                         position: "relative", 
                         mb: 0, 
                         pb: 0,
                         border: `1px solid ${theme.palette.divider}`,
                         borderRadius: 1,
                         p: 1,
                         backgroundColor: theme.palette.background.default,
                       }}
                     >
                       <Line
                         data={createChartData(config.field, config.title)}
                         plugins={[createTrackingLinePlugin(trackingX, setTrackingX, theme)]}
                         options={chartOptions}
                       />
                     </Box>
                     <Box 
                       sx={{ 
                         mt: 1, 
                         height: 200, 
                         position: "relative", 
                         pt: 0,
                         border: `1px solid ${theme.palette.divider}`,
                         borderRadius: 1,
                         p: 1,
                         backgroundColor: theme.palette.background.default,
                       }}
                     >
                       <Line
                         data={createDeltaChartData(config.deltaField)}
                         plugins={[createTrackingLinePlugin(trackingX, setTrackingX, theme)]}
                         options={deltaChartOptions}
                       />
                     </Box>
                     <Box 
                       sx={{ 
                         mt: 1, 
                         height: 200, 
                         position: "relative", 
                         pt: 0,
                         border: `1px solid ${theme.palette.divider}`,
                         borderRadius: 1,
                         p: 1,
                         backgroundColor: theme.palette.background.default,
                       }}
                     >
                       <Line
                         data={createExpansionContractionChartData(config.field)}
                         plugins={[
                           createZeroLinePlugin(theme),
                           createTrackingLinePlugin(trackingX, setTrackingX, theme)
                         ]}
                         options={{
                           ...expansionContractionChartOptions,
                           plugins: {
                             ...expansionContractionChartOptions.plugins,
                             zeroLinePlugin: {
                               enabled: true,
                             },
                           },
                         }}
                       />
                     </Box>
                   </Paper>
                   {index < chartConfigs.length - 1 && (
                     <Divider sx={{ my: 4, borderWidth: 2, borderColor: theme.palette.divider }} />
                   )}
                 </Box>
               ))}
             </Box>
           )}
        </Box>
      </DialogContent>

    </Dialog>
  );
};

export default BondDetailsModal;
